import { FormValues, getValue, InputFormGroup, InputFormSidePanelComponent } from "o4a-react";
import * as React from "react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import {
  AdbsEditNetworkingValues,
  AdbsNetworkingEditPanel,
  Fields,
} from "../../components/AdbsNetworkingEditPanel/AdbsNetworkingEditPanel";
import { UpdateAdbsDatabaseDetails } from "../../gen/clients/mchub-azure-api-client";
import { parseId } from "../../helpers/idHelper";
import { ACCESS_TYPES } from "../../helpers/resourceHelper";
import { useFeatures } from "../../hooks/useFeatures";
import { useMutationCall } from "../../hooks/useMutationCall";
import {
  ADBS_UPDATE_POLL_DELAY,
  ADBS_UPDATE_POLL_INTERVAL,
  AsyncNotificationMethodKey,
  AsyncNotificationPolledResponseKey,
} from "../../models/AsyncNotificationProviders";
import { getOciRegion } from "../../utils";
import { OperationActionProps } from "../OperationTypes";

export interface AdbsEditNetworkAccessProps extends OperationActionProps {
  databaseId: string | undefined;
  adbsEditNetworkingValues: AdbsEditNetworkingValues;
}

const AdbsEditNetworkAccess = (
  { targetId, location, databaseId, adbsEditNetworkingValues, onExecute, onCancel }
    : AdbsEditNetworkAccessProps,
): JSX.Element => {
  const { subscriptionId, resourceGroup, resourceName } = parseId(databaseId);
  const resGroup = decodeURIComponent(resourceGroup || "");
  const resName = decodeURIComponent(resourceName || "");

  const { enableAdbsPE } = useFeatures();

  const [sidePanelRef, setSidePanelRef] = React.useState<InputFormSidePanelComponent>(
    {} as InputFormSidePanelComponent,
  );

  const { invokeCall } = useMutationCall(
    { method: apiClients.withRegion(getOciRegion(location)).adbsDatabaseApi.updateAdbsDatabase },
  );

  const onSubmit = (formValues: FormValues): void => {
    let whitelistedIps: string[] = [];
    let vnetId = null;
    let subnetId = null;
    const accessType = getValue(formValues, Fields.AccessType, InputFormGroup)?.id;
    const isRouteVNET = getValue(formValues, Fields.RouteVNET, InputFormGroup);
    const isAccessControlEnabled = accessType === ACCESS_TYPES.RESTRICTED;
    if (isAccessControlEnabled) {
      whitelistedIps = getValue(formValues, Fields.IPsCidrs, InputFormGroup)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .filter((ele: any) => ele.address !== undefined)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((ele: any) => ele.address);
    }
    if (enableAdbsPE && !isAccessControlEnabled) {
      vnetId = isRouteVNET ? getValue(formValues, Fields.VNET, InputFormGroup)?.[0].id : "";
      subnetId = isRouteVNET ? getValue(formValues, Fields.Subnet, InputFormGroup)?.[0].id : "";
    }

    const invokeOptions = {
      onSuccess: onExecute,
      onFailure: () => {
        sidePanelRef.allowResubmit();
      },
      notification: {
        inProgress: {
          title: Messages.notifications.inProgress.titles.updateAdbsNetworking(),
          message: Messages.notifications.inProgress.messages.updateAdbsNetworking(resName),
        },
        success: {
          title: Messages.notifications.success.titles.updateAdbsNetworking(),
          message: Messages.notifications.success.messages.updateAdbsNetworking(resName),
        },
        failure: {
          title: Messages.notifications.failure.titles.updateAdbsNetworking(),
          message: Messages.notifications.failure.messages.updateAdbsNetworking(resName),
        },
        asyncPolling: {
          methodKey: AsyncNotificationMethodKey.ADBS_GET,
          methodArgs: { subscriptionId, resourceGroupName: resGroup, databaseName: resName },
          location,
          responseMethodKey: AsyncNotificationPolledResponseKey.ADBS_UPDATED_CHECK,
          delay: ADBS_UPDATE_POLL_DELAY,
          interval: ADBS_UPDATE_POLL_INTERVAL,
        },
      },
    };
    invokeCall({
      subscriptionId,
      resourceGroupName: resGroup,
      databaseName: resName,
      updateAdbsDatabaseDetails: {
        whitelistedIps,
        vnetId: enableAdbsPE ? vnetId : undefined,
        subnetId: enableAdbsPE ? subnetId : undefined,
        isAccessControlEnabled,
      } as UpdateAdbsDatabaseDetails,
    }, invokeOptions);
  };

  return (
    <AdbsNetworkingEditPanel
      targetId={targetId}
      adbsEditNetworkingValues={adbsEditNetworkingValues}
      onSubmit={onSubmit}
      componentRef={setSidePanelRef}
      onClose={() => onCancel?.()}
      subscriptionId={subscriptionId}
      location={location}
    />
  );
};

export const newAdbsEditNetworkAccess = (
  props: AdbsEditNetworkAccessProps,
): JSX.Element => (<AdbsEditNetworkAccess {...props} />);
